import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { CreateSfBankAccountGeneralInfoDto, ISfBankAccount } from '@sparling/financial-common'
import { useDispatch, useSelector } from 'react-redux'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { InputSelect } from '../../../../components/InputSelect'
import { InputField } from '../../../../components/InputField'
import { Modal } from '../../../../components/Modal'
import { createSfBankAccountRequest, updateSfBankAccountRequest } from '../../../../store/actions'
import { CreateSfBankAccountFeesResolver } from './resolvers'
import { fiatsEnumSelector } from '../../../../store/selectors'

interface Props {
  isOpen: boolean
  modalClose: () => void
  fields: ISfBankAccount
}

enum FormSteps {
  generalInfo = 'generalInfo',
  feesInfo = 'feesInfo',
}

export const BankAccountsModal: FC<Props> = ({ isOpen, fields, modalClose }) => {
  const [formStep, setFormStep] = useState<FormSteps>(FormSteps.generalInfo)
  const [formValues, setFormValues] = useState<ISfBankAccount>(fields || {})
  const dispatch = useDispatch()
  const fiatsEnum = useSelector(fiatsEnumSelector)

  const formMethods = useForm<ISfBankAccount>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: {
      [FormSteps.generalInfo]: classValidatorResolver(CreateSfBankAccountGeneralInfoDto),
      [FormSteps.feesInfo]: CreateSfBankAccountFeesResolver,
    }[formStep],
  })

  const {
    handleSubmit,
    formState: { isValid },
    getValues,
    setValue,
    reset,
  } = formMethods

  useEffect(() => {
    if (!!fields && Object.keys(fields).length > 0) {
      setFormValues((prev) => {
        if (Object.keys(prev).length === 0) {
          return fields
        }

        return prev
      })
    }
  }, [fields])

  useEffect(() => {
    reset()
    Object.entries(formValues).forEach(([key, value]) => {
      setValue(key as keyof ISfBankAccount, value)
    })
  }, [formStep, formValues, getValues, reset, setValue])

  const formSubmitHandle = useCallback(
    (data: ISfBankAccount) => {
      if ('id' in data) {
        dispatch(updateSfBankAccountRequest(data))
      } else {
        dispatch(createSfBankAccountRequest(data))
      }

      reset()
      modalClose?.()
    },
    [dispatch, modalClose, reset],
  )

  const prevStepHandle = useCallback(() => {
    setFormValues((prev) => ({ ...prev, ...getValues() }))
    setFormStep(FormSteps.generalInfo)
  }, [getValues])

  const nextStepHandle = useCallback(() => {
    setFormValues((prev) => ({ ...prev, ...getValues() }))
    setFormStep(FormSteps.feesInfo)
  }, [getValues])

  const { onSubmit, onCancel, submitTitle, cancelTitle } = useMemo(
    () =>
      ({
        [FormSteps.generalInfo]: {
          onSubmit: nextStepHandle,
          onCancel: modalClose,
          submitTitle: 'Next',
          cancelTitle: 'Cancel',
        },
        [FormSteps.feesInfo]: {
          onSubmit: handleSubmit(formSubmitHandle),
          onCancel: prevStepHandle,
          submitTitle: 'Submit',
          cancelTitle: 'Back',
        },
      }[formStep]),
    [formStep, nextStepHandle, prevStepHandle, handleSubmit, formSubmitHandle, modalClose],
  )

  return (
    <Modal
      title={fields ? 'Edit Account' : 'Add Account'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitTitle={submitTitle}
      cancelTitle={cancelTitle}
      isSubmitDisabled={!isValid}
      width={680}
      submitButtonProps={{
        type: 'button',
      }}
    >
      {formStep === FormSteps.generalInfo ? (
        <FormProvider {...formMethods}>
          <Grid container spacing={2.5} mb={4}>
            <Grid item xs={12} sm={6} container direction="column">
              <InputSelect
                key="currency"
                labelName="Currency"
                inputName="currency"
                placeholder="Please Select"
                enums={fiatsEnum}
                width={300}
                mb={20}
                defaultValue={formValues?.currency}
              />
              <InputField
                key="bankName"
                labelName="Bank Name"
                inputName="bankName"
                inputType="text"
                defaultValue={formValues?.bankName}
              />
              <InputField
                key="bankSwift"
                labelName="Bank SWIFT/BIC"
                inputName="bankSwift"
                inputType="text"
                defaultValue={formValues?.bankSwift}
              />
              <InputField
                key="bankAccountNumber"
                labelName="Bank Account Number"
                inputName="bankAccountNumber"
                inputType="text"
                defaultValue={formValues?.bankAccountNumber}
              />
              <InputField
                key="bankAddress"
                labelName="Bank Address"
                inputName="bankAddress"
                inputType="text"
                defaultValue={formValues?.bankAddress}
              />
            </Grid>

            <Grid item xs={12} sm={6} container direction="column">
              <InputField
                key="beneficiaryName"
                labelName="Beneficiary Name"
                inputName="beneficiaryName"
                inputType="text"
                defaultValue={formValues?.beneficiaryName}
              />
              <InputField
                key="intermediaryBankName"
                labelName="Intermediary Bank Name"
                inputName="intermediaryBankName"
                inputType="text"
                defaultValue={formValues?.intermediaryBankName}
              />
              <InputField
                key="intermediaryBankSwift"
                labelName="Intermediary Bank SWIFT/BIC"
                inputName="intermediaryBankSwift"
                inputType="text"
                defaultValue={formValues?.intermediaryBankSwift}
              />
              <InputField
                key="intermediaryBankAddress"
                labelName="Intermediary Bank Address"
                inputName="intermediaryBankAddress"
                inputType="text"
                defaultValue={formValues?.intermediaryBankAddress}
              />
              <InputField
                key="referenceToInclude"
                labelName="Reference to Include"
                inputName="referenceToInclude"
                inputType="text"
                defaultValue={formValues?.referenceToInclude}
              />
            </Grid>
            <Grid item mt={-3} mb={3} xs={12}>
              <InputField
                key="description"
                labelName="Description"
                inputName="description"
                inputType="text"
                rows={4}
                multiline={true}
                fullWidth={true}
                withoutPadding={true}
                defaultValue={formValues?.description}
              />
            </Grid>
          </Grid>
        </FormProvider>
      ) : (
        <FormProvider {...formMethods}>
          <Grid container direction="column" mb={4}>
            <Grid item direction="column" xs={12}>
              <InputField
                key="moneyInFees"
                labelName="Money In Fees"
                inputName="moneyInFees"
                inputType="text"
                defaultValue={formValues?.moneyInFees}
                fullWidth
              />
            </Grid>
            <Grid item direction="column" xs={12}>
              <InputField
                key="moneyOutFees"
                labelName="Money Out Fees"
                inputName="moneyOutFees"
                inputType="text"
                defaultValue={formValues?.moneyOutFees}
                fullWidth
              />
            </Grid>
            <Grid item direction="column" xs={12}>
              <InputField
                key="wireFees"
                labelName="Wire Fees"
                inputName="wireFees"
                inputType="text"
                defaultValue={formValues?.wireFees}
                fullWidth
              />
            </Grid>
          </Grid>
        </FormProvider>
      )}
    </Modal>
  )
}
